<template>
  <el-table
    ref="table"
    stripe
    :data="data"
    :default-sort="defaultSort"
    @sort-change="$emit('sort-change', $event)"
    @selection-change="$emit('selection-change', $event)"
    @select="changeSelectAll"
    @select-all="changeSelectAll"
  >
    <template #empty>
      <span v-html="defaultEmptyText"></span>
    </template>
    <el-table-column v-if="selectionColumn" type="selection" width="50" align="center" />
    <el-table-column prop="domain" sortable="custom">
      <template #default="{ row }">
        <a
          :class="getClassList(row, 'domain')"
          :href="`//${row.domain}`"
          class="link"
          target="_blank"
          rel="noopener noreferrer"
        >{{ row.domain }}</a>
      </template>
      <template #header="{ column }">
        <el-tooltip :content="$t('c.table.columnTooltipDomain')" placement="top">
          <span>{{ $t("c.table.columnNameDomain") }}</span>
        </el-tooltip>
      </template>
    </el-table-column>
    <el-table-column v-if="campaignColumn" prop="campaign" sortable="custom">
      <template #default="{ row }">{{ getCampaignName(row.campaign) }}</template>
      <template #header="{ column }">
        <el-tooltip :content="$t('c.table.columnTooltipCampaign')" placement="top">
          <span>{{ $t("c.table.columnNameCampaign") }}</span>
        </el-tooltip>
      </template>
    </el-table-column>
    <el-table-column prop="impressions" sortable="custom">
      <template #default="{ row }">{{ row.impressions !== null ? row.impressions : '—' }}</template>
      <template #header="{ column }">
        <el-tooltip :content="$t('c.table.columnTooltipPlacementsImpressions')" placement="top">
          <span>{{ $t("c.table.columnNameImpressions") }}</span>
        </el-tooltip>
      </template>
    </el-table-column>
    <el-table-column prop="ctr" sortable="custom">
      <template #default="{ row }">
        <span
          :class="getClassList(row, 'ctr')"
        >{{ row.ctr !== null ? row.ctr : '—' }}</span>
      </template>
      <template #header="{ column }">
        <el-tooltip :content="$t('c.table.columnTooltipCTR')" placement="top">
          <span>{{ $t("c.table.columnNameCTR") }}</span>
        </el-tooltip>
      </template>
    </el-table-column>
    <el-table-column prop="clicks" sortable="custom">
      <template #default="{ row }">
        <span
          :class="getClassList(row, 'clicks')"
        >{{ row.clicks !== null ? row.clicks : '—' }}</span>
      </template>
      <template #header="{ column }">
        <el-tooltip :content="$t('c.table.columnTooltipPlacementsClicks')" placement="top">
          <span>{{ $t("c.table.columnNameClicks") }}</span>
        </el-tooltip>
      </template>
    </el-table-column>
    <el-table-column prop="cpc" sortable="custom">
      <template #default="{ row }">{{ row.cpc !== null ? row.cpc : '—' }}</template>
      <template #header="{ column }">
        <el-tooltip :content="$t('c.table.columnTooltipCPC')" placement="top">
          <span>{{ $t("c.table.columnNameCPC") }}</span>
        </el-tooltip>
      </template>
    </el-table-column>
    <el-table-column prop="conv_rate" sortable="custom">
      <template #default="{ row }">{{ row.convRate !== null ? row.convRate : '—' | toFixed(1) }}</template>
      <template #header="{ column }">
        <el-tooltip :content="$t('c.table.columnTooltipCR')" placement="top">
          <span>{{ $t("c.table.columnNameCR") }}</span>
        </el-tooltip>
      </template>
    </el-table-column>
    <el-table-column prop="conversions" sortable="custom">
      <template #default="{ row }">{{ row.conversions !== null ? row.conversions : '—' }}</template>
      <template #header="{ column }">
        <el-tooltip :content="$t('c.table.columnTooltipConversions')" placement="top">
          <span>{{ $t("c.table.columnNameConversions") }}</span>
        </el-tooltip>
      </template>
    </el-table-column>
    <el-table-column prop="cost" sortable="custom">
      <template #default="{ row }">{{ row.cost !== null ? row.cost : '—' }}</template>
      <template #header="{ column }">
        <el-tooltip :content="$t('c.table.columnTooltipCost')" placement="top">
          <span>{{ $t("c.table.columnNameCost") }}</span>
        </el-tooltip>
      </template>
    </el-table-column>
    <el-table-column prop="cost_conv" sortable="custom">
      <template #default="{ row }">
        <span
          :class=" getClassList(row, 'cpl')"
        >{{ row.costConv !== null ? row.costConv : '—' }}</span>
      </template>
      <template #header="{ column }">
        <v-cpl-tooltip :cpl="cpl" />
      </template>
    </el-table-column>
    <el-table-column v-if="damageColumn" prop="damage" sortable="custom">
      <template #default="{ row }">{{ row.damage }}</template>
      <template #header="{ column }">
        <el-tooltip :content="$t('c.table.columnTooltipDamage')" placement="top">
          <span>{{ $t("c.table.columnNameDamage") }}</span>
        </el-tooltip>
      </template>
    </el-table-column>
    <el-table-column v-if="classificationColumn" prop="classification_code" sortable="custom">
      <template #default="{ row }">{{ getHumanReadableReason(row.classificationCode) }}</template>
      <template #header="{ column }">
        <el-tooltip :content="banColumnTooltip" placement="top">
          <span>{{ banColumnTitle }}</span>
        </el-tooltip>
      </template>
    </el-table-column>
  </el-table>
</template>

<script>
import VCplTooltip from '@/components/common/VCplTooltip.vue'
import tableMixin from '@/mixins/table'
import tableFormatMixin from '@/mixins/tableFormat'
import tableBanReasonMixin from '@/mixins/tableBanReason'
import * as types from '@/store/types'

export default {
  name: 'VPlacementsTable',
  components: { VCplTooltip },

  mixins: [tableMixin, tableFormatMixin, tableBanReasonMixin],

  props: {
    data: {
      type: Array,
      required: true
    },
    defaultSort: {
      type: Object,
      required: true,
      validator(defaultSort) {
        const props = ['prop', 'order']
        const sortProps = Object.getOwnPropertyNames(defaultSort)

        return props.every(prop => sortProps.includes(prop))
      }
    },
    selectionColumn: {
      type: Boolean,
      default: false
    },
    cpl: {
      type: [Object, Number]
    },
    tableType: {
      type: String,
      required: true,
      validator: value => [
        types.WORST_PLACEMENTS,
        types.DISPUTED_PLACEMENTS,
        types.BEST_PLACEMENTS,
        types.ACTION_LOG_PLACEMENTS
      ].includes(value)
    },
    damageColumn: {
      type: Boolean,
      default: false
    },
    campaignColumn: {
      type: Boolean,
      default: true
    },
    highlightReasonProperty: {
      type: Boolean,
      default: false
    },
    classificationColumn: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    banColumnTooltip() {
      if (this.tableType === types.ACTION_LOG_PLACEMENTS) {
        return this.$t('c.table.columnNameBanReason')
      }
      return this.$t('c.table.columnTooltipReason')
    },
    banColumnTitle() {
      if (this.tableType === types.ACTION_LOG_PLACEMENTS) {
        return this.$t('c.table.columnNameBanReason')
      }
      return this.$t('c.table.columnNameReason')
    }
  }
}
</script>
